import React from 'react';
import { Chip, Divider, Icon, IconButton, LinearProgress, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { useNavigate, useParams } from 'react-router-dom';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import LungWeatherService from '../../hooks/LungWeatherService';
import {
  LungWeatherMeasure,
  LungWeatherPattern,
  LungWeatherReview as LungWeatherReviewModel,
  LungWeatherStable,
  bearingToCompass,
  stableTypeLabels
} from '../../models/lung-weather';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { Card, CardContent, CardHeader, CardTitle } from '../../elements/ui/card';
import Vane from './Vane';
import TrackRecord from './TrackRecord';
import { lungWeatherColors } from './colors';

const service = new LungWeatherService();

/**
 * What the lung/weather correlation has learned for one disease, and what it makes of this week.
 *
 * Until this existed the two tables were written on every worker pass and read by nobody, which made "it never
 * warned me" impossible to tell apart from "it never ran". The page leads with the verdict, then the pattern
 * ranking behind it, then the track record - in that order, because a farmer wants the answer first and the
 * evidence second.
 */
const LungWeatherReview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { diseaseId } = useParams<{ diseaseId: string }>();
  const { formatDate } = useFarmDateTime();

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [review, setReview] = React.useState<LungWeatherReviewModel | null>(null);
  const [selectedStable, setSelectedStable] = React.useState<number>(0);

  const load = React.useCallback(() => {
    if (!diseaseId) {
      return;
    }

    setIsLoading(true);

    service
      .getReview(diseaseId)
      .then((result) => {
        setReview(result);
        setSelectedStable(result.stables.length > 0 ? result.stables[0].stable : 0);
      })
      .catch((e) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })))
      .finally(() => setIsLoading(false));
  }, [diseaseId, t]);

  React.useEffect(() => load(), [load]);

  if (isLoading) {
    return (
      <div className="p-6">
        <LinearProgress />
        <div className="mt-4 flex flex-col gap-4">
          <Skeleton height={60} animation="wave" />
          <Skeleton height={220} animation="wave" />
          <Skeleton height={140} animation="wave" />
        </div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="p-6">
        <Typography variant="body1">{t('f-com-no-data')}</Typography>
      </div>
    );
  }

  const stable = review.stables.find((s) => s.stable === selectedStable) ?? review.stables[0];

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Tooltip arrow title={t('f-com-back') ?? ''}>
              <IconButton size="small" onClick={() => navigate(-1)} sx={V4_ICON_BUTTON_SMALL}>
                <Icon>arrow_back</Icon>
              </IconButton>
            </Tooltip>
            <Icon color="primary">air</Icon>
            <Typography variant="h6" color="primary">
              {t('f-lung-weather-title', { disease: review.diseaseName })}
            </Typography>
          </div>

          <div className="flex items-center gap-2">
            {review.sensitivity && (
              <Chip
                size="small"
                variant="outlined"
                label={t('f-lung-weather-sensitivity-chip', {
                  value: t(`f-notif-rule-sensitivity-${review.sensitivity.toLowerCase()}`)
                })}
              />
            )}
            <IconButton color="primary" size="small" onClick={load} sx={V4_ICON_BUTTON_SMALL}>
              <Icon>refresh</Icon>
            </IconButton>
          </div>
        </div>

        <Typography variant="body2" color="textSecondary">
          {t('f-lung-weather-intro', { farm: review.farmName })}
        </Typography>
      </header>

      {review.stables.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <Typography variant="subtitle1">{t('f-lung-weather-empty-title')}</Typography>
            <Typography variant="body2" color="textSecondary">
              {review.notificationRuleId ? t('f-lung-weather-empty-waiting') : t('f-lung-weather-empty-no-rule')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {review.stables.length > 1 && (
            <div>
              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                value={selectedStable}
                onChange={(_, value) => setSelectedStable(value)}
                indicatorColor="primary"
                textColor="primary"
              >
                {review.stables.map((s) => (
                  <Tab key={s.stable} value={s.stable} label={t(stableTypeLabels[s.stable] ?? 'f-com-unknown')} />
                ))}
              </Tabs>
              <Divider />
            </div>
          )}

          {stable && <StableReview review={review} stable={stable} formatDate={formatDate} />}
        </>
      )}
    </div>
  );
};

const StableReview: React.FC<{
  review: LungWeatherReviewModel;
  stable: LungWeatherStable;
  formatDate: (value: string) => string;
}> = ({ review, stable, formatDate }) => {
  const { t } = useTranslation();

  const latest = stable.latest;
  const closest = stable.patterns.find((pattern) => pattern.isClosestMatch);
  const learning = latest?.verdict === 'Learning';

  const directionSpread = latest?.measures.find((measure) => measure.measure === 'Direction')?.referenceSpread ?? 30;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="flex flex-col gap-5 lg:col-span-7">
              {latest ? (
                <>
                  <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
                    <div className="flex items-end gap-2">
                      <span className="text-5xl font-semibold leading-none" style={{ color: lungWeatherColors.accent }}>
                        {Math.round(latest.matchScore)}
                      </span>
                      <span className="pb-1 text-sm text-gray-500">{t('f-lung-weather-out-of-match')}</span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs uppercase tracking-wide text-gray-400">
                        {t('f-lung-weather-confidence')}
                      </span>
                      <span className="text-lg font-medium text-gray-900">{Math.round(latest.confidence * 100)}%</span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs uppercase tracking-wide text-gray-400">
                        {t('f-lung-weather-warns-at-label')}
                      </span>
                      <span className="text-lg font-medium text-gray-900">{Math.round(latest.matchThreshold)}</span>
                    </div>
                  </div>

                  <Typography variant="body1">
                    {latest.verdict === 'Skipped'
                      ? t('f-lung-weather-verdict-skipped', { reason: latest.skipReason ?? '' })
                      : closest
                        ? t('f-lung-weather-verdict-resembles', {
                            date: formatDate(closest.detectedAt),
                            count: closest.peakInjectionCount
                          })
                        : t('f-lung-weather-verdict-no-match')}
                  </Typography>

                  {learning && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <Typography variant="body2" color="textSecondary">
                        {t('f-lung-weather-learning', {
                          have: stable.patterns.length,
                          need: review.patternsForConfidence
                        })}
                      </Typography>
                    </div>
                  )}

                  {latest.verdict === 'Matched' && latest.notificationDispatched === false && (
                    <div>
                      <Chip size="small" variant="outlined" label={t('f-lung-weather-not-delivered')} />
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    {latest.measures.map((measure) => (
                      <MeasureBar key={measure.measure} measure={measure} />
                    ))}
                  </div>

                  <span className="text-xs text-gray-400">
                    {t('f-lung-weather-checked-at', { date: formatDate(latest.checkedAt) })}
                  </span>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t('f-lung-weather-not-compared-yet')}
                </Typography>
              )}
            </div>

            <div className="flex flex-col items-center gap-3 lg:col-span-5">
              <Vane patterns={stable.patterns} forecast={latest?.forecast} toleranceDegrees={directionSpread} />
              {latest?.forecast && (
                <span className="text-sm font-medium" style={{ color: lungWeatherColors.accent }}>
                  {t('f-lung-weather-vane-forecast', {
                    degrees: Math.round(latest.forecast.dominantWindDirectionDegrees),
                    compass: bearingToCompass(latest.forecast.dominantWindDirectionDegrees),
                    gust: Math.round(latest.forecast.maxWindGustKmh)
                  })}
                </span>
              )}
              <p className="max-w-xs text-center text-xs leading-relaxed text-gray-500">
                {t('f-lung-weather-vane-caption')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>{t('f-lung-weather-patterns-title')}</CardTitle>
          <p className="text-xs text-gray-500">{t('f-lung-weather-patterns-hint')}</p>
        </CardHeader>
        <CardContent className="px-0">
          {/* A real grid with shared column widths, so the header and every row line up rather than each row
              distributing its own cells. */}
          <div className="hidden grid-cols-12 gap-4 border-b border-gray-200 px-6 pb-2 text-xs uppercase tracking-wide text-gray-400 sm:grid">
            <span className="col-span-3">{t('f-lung-weather-col-recorded')}</span>
            <span className="col-span-2">{t('f-lung-weather-col-from')}</span>
            <span className="col-span-2">{t('f-lung-weather-col-gust')}</span>
            <span className="col-span-2">{t('f-lung-weather-col-treatments')}</span>
            <span className="col-span-3 text-right">{t('f-lung-weather-col-match')}</span>
          </div>

          {stable.patterns.map((pattern) => (
            <PatternRow key={pattern.id} pattern={pattern} formatDate={formatDate} />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>{t('f-lung-weather-track-record-title')}</CardTitle>
          <p className="text-xs text-gray-500">{t('f-lung-weather-track-record-hint')}</p>
        </CardHeader>
        <CardContent>
          <TrackRecord
            history={stable.history}
            patterns={stable.patterns}
            threshold={latest?.matchThreshold ?? review.matchThreshold}
          />
        </CardContent>
      </Card>
    </div>
  );
};

const MeasureBar: React.FC<{ measure: LungWeatherMeasure }> = ({ measure }) => {
  const { t } = useTranslation();

  const discounted = measure.appliedWeight <= 0.001;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm text-gray-700">{t(`f-wind-measure-${measure.measure.toLowerCase()}`)}</span>
        <span className="text-xs tabular-nums text-gray-500">
          {t('f-lung-weather-measure-values', {
            forecast: Math.round(measure.forecastValue),
            pattern: Math.round(measure.patternValue)
          })}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div className="h-1.5 flex-1 rounded-full" style={{ background: lungWeatherColors.grid }}>
          <div
            className="h-1.5 rounded-full"
            style={{
              width: `${Math.max(0, Math.min(100, measure.similarity))}%`,
              background: discounted ? lungWeatherColors.muted : lungWeatherColors.accent
            }}
          />
        </div>
        <span className="w-8 text-right text-sm tabular-nums text-gray-900">{Math.round(measure.similarity)}</span>
      </div>

      {/* A measure discounted to nothing says so, rather than looking like it agreed. */}
      {discounted && <span className="text-xs text-gray-400">{t('f-lung-weather-measure-not-counted')}</span>}
    </div>
  );
};

const PatternRow: React.FC<{ pattern: LungWeatherPattern; formatDate: (value: string) => string }> = ({
  pattern,
  formatDate
}) => {
  const { t } = useTranslation();

  // A pattern resting on very few treatments is flagged: it is a legitimate peak under the farmer's own threshold
  // and still weak evidence, and that distinction is exactly what confidence is made of. Amber rather than red -
  // it is a caveat, not a fault.
  const thin = pattern.evidenceWeight < 0.5;

  return (
    <div
      className="grid grid-cols-2 items-center gap-x-4 gap-y-1 border-b border-gray-100 px-6 py-3 last:border-b-0 sm:grid-cols-12"
      style={pattern.isClosestMatch ? { background: lungWeatherColors.accentSoft } : undefined}
    >
      <span
        className="col-span-2 text-sm sm:col-span-3"
        style={pattern.isClosestMatch ? { color: lungWeatherColors.accent, fontWeight: 600 } : undefined}
      >
        {formatDate(pattern.detectedAt)}
      </span>

      <span className="text-xs tabular-nums text-gray-600 sm:col-span-2">
        {Math.round(pattern.wind.dominantWindDirectionDegrees)}°{' '}
        {bearingToCompass(pattern.wind.dominantWindDirectionDegrees)}
      </span>

      <span className="text-xs tabular-nums text-gray-600 sm:col-span-2">
        {t('f-lung-weather-gust-value', { value: Math.round(pattern.wind.maxWindGustKmh) })}
      </span>

      <span
        className="text-xs tabular-nums sm:col-span-2"
        style={{ color: thin ? lungWeatherColors.warning : undefined }}
      >
        {t('f-lung-weather-treatments-value', { value: pattern.peakInjectionCount })}
      </span>

      <div className="col-span-2 flex items-center gap-3 sm:col-span-3">
        {pattern.scoreAgainstForecast === undefined ? (
          <span className="ml-auto text-xs text-gray-400">—</span>
        ) : (
          <>
            <div className="h-1 flex-1 rounded-full" style={{ background: lungWeatherColors.grid }}>
              <div
                className="h-1 rounded-full"
                style={{
                  width: `${Math.max(0, Math.min(100, pattern.scoreAgainstForecast))}%`,
                  background: pattern.isClosestMatch ? lungWeatherColors.accent : lungWeatherColors.muted
                }}
              />
            </div>
            <span
              className="w-8 text-right text-sm tabular-nums"
              style={{
                color: pattern.isClosestMatch ? lungWeatherColors.accent : undefined,
                fontWeight: pattern.isClosestMatch ? 600 : undefined
              }}
            >
              {Math.round(pattern.scoreAgainstForecast)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default LungWeatherReview;
